<template>
    <div class="d-flex-grow justify-center">
         <v-form @submit.prevent="store" ref="formRef" v-model="formValid" class="w-100 w-md-50rem max-w-100">
             <v-card class="d-flex-col-grow">
                 <v-card-text class="flex-grow-1">
                     <div class="d-flex flex-column ga-7">
                         <div class="fs-2 fw-medium">
                             Bank account Information
                         </div>
                         <div v-if="form.errors.custom_error" class="fs-1 text-error">
                             {{ form.errors.custom_error }}
                         </div>
                         <div>
                             <v-text-field-v1
                                 label="Nickname"
                                 v-model="form.nickname"
                                 placeholder=""
                                 :error-messages="form.errors.nickname"
                             ></v-text-field-v1>
                         </div>
                         <div>
                             <v-text-field-v1
                                 label="Bank name"
                                 v-model="form.bank_name"
                                 placeholder=""
                                 :error-messages="form.errors.bank_name"
                             ></v-text-field-v1>
                         </div>
                         <div>
                             <v-text-field-v1
                                 label="Account name"
                                 v-model="form.account_name"
                                 placeholder=""
                                 :error-messages="form.errors.account_name"
                             ></v-text-field-v1>
                         </div>
                         <div>
                             <v-text-field-v1
                                 label="Account number"
                                 v-model="form.account_number"
                                 placeholder=""
                                 :error-messages="form.errors.account_number"
                             ></v-text-field-v1>
                         </div>
                         <div>
                             <v-text-field-v1
                                 label="Routing number"
                                 v-model="form.routing_number"
                                 placeholder=""
                                 :error-messages="form.errors.routing_number"
                             ></v-text-field-v1>
                         </div>
                         <div>
                             <v-text-field-v1
                                 label="Swift code"
                                 v-model="form.swift_code"
                                 placeholder=""
                                 :error-messages="form.errors.swift_code"
                             ></v-text-field-v1>
                         </div>
                         <div class="d-flex flex-column ga-3">
                            <div>
                                <v-switch-v1
                                    v-model="form.is_meow"
                                    label="Meow"
                                    ></v-switch-v1>
                            </div>
                            <div v-if="form.is_meow">
                                <v-text-field-v1
                                    label="Meow Account ID"
                                    v-model="form.meow_account_id"
                                    placeholder=""
                                    :error-messages="form.errors.meow_account_id"
                                ></v-text-field-v1>
                            </div>
                        </div>

                        <div class="d-flex flex-column ga-2" v-if="bankAccount?.id && hasProvider">
                            <div class="fs-1 fw-medium">Reconciliation automation</div>
                            <div class="text-medium-emphasis fs--1">
                                When on, the cron pulls new credits from the linked provider and matches them against pending invoices. Turn off to pause without disconnecting.
                            </div>
                            <v-switch
                                :model-value="automationEnabled"
                                @update:modelValue="onAutomationToggle"
                                :label="automationEnabled ? 'Automation: ON' : 'Automation: OFF'"
                                :loading="automationLoading"
                                :disabled="automationLoading"
                                color="primary"
                                hide-details
                                density="compact"
                            ></v-switch>
                        </div>

                        <div class="d-flex flex-column ga-3" v-if="bankAccount?.id">
                            <div class="fs-1 fw-medium">Plaid</div>
                            <div v-if="!plaidConnected" class="d-flex flex-column ga-2">
                                <v-checkbox
                                    v-model="consent"
                                    density="compact"
                                    hide-details
                                    label="I authorize Easy Invoice to connect this bank account via Plaid to reconcile payments against invoices."
                                ></v-checkbox>
                                <div>
                                    <v-btn-input-compact
                                        @click="connectPlaid"
                                        :disabled="!consent || plaidLoading"
                                        :loading="plaidLoading"
                                    >
                                        <template #prepend>
                                            <mdi-icon>link</mdi-icon>
                                        </template>
                                        Connect with Plaid
                                    </v-btn-input-compact>
                                </div>
                            </div>
                            <div v-else class="d-flex flex-column ga-2">
                                <div>
                                    Connected account:
                                    <code>{{ bankAccount?.additional_data?.plaid_account_id }}</code>
                                </div>
                                <div v-if="bankAccount?.additional_data?.plaid_consent_at" class="text-medium-emphasis fs--1">
                                    Consent recorded {{ bankAccount?.additional_data?.plaid_consent_at }}
                                </div>
                                <div>
                                    <v-btn-input-compact color="btn-secondary" @click="disconnectPlaid" :loading="plaidLoading">
                                        <template #prepend>
                                            <mdi-icon>link_off</mdi-icon>
                                        </template>
                                        Disconnect Plaid
                                    </v-btn-input-compact>
                                </div>
                            </div>
                        </div>

                        <div class="d-flex flex-column ga-3" v-if="bankAccount?.id">
                            <div class="fs-1 fw-medium">SimpleFin</div>
                            <div v-if="!simplefinConnected" class="d-flex flex-column ga-2">
                                <div class="text-medium-emphasis fs--1">
                                    Generate a Setup Token at
                                    <a href="https://beta-bridge.simplefin.org/" target="_blank" rel="noopener">beta-bridge.simplefin.org</a>
                                    after linking your bank, then paste it below.
                                </div>
                                <v-textarea
                                    label="SimpleFin Setup Token"
                                    v-model="sfSetupToken"
                                    rows="3"
                                    auto-grow
                                    density="compact"
                                ></v-textarea>
                                <v-checkbox
                                    v-model="sfConsent"
                                    density="compact"
                                    hide-details
                                    label="I authorize Easy Invoice to use this SimpleFin Access URL to pull bank transactions for invoice reconciliation."
                                ></v-checkbox>
                                <div>
                                    <v-btn-input-compact
                                        @click="connectSimplefin"
                                        :disabled="!sfConsent || !sfSetupToken || sfLoading"
                                        :loading="sfLoading"
                                    >
                                        <template #prepend>
                                            <mdi-icon>link</mdi-icon>
                                        </template>
                                        Connect SimpleFin
                                    </v-btn-input-compact>
                                </div>
                            </div>
                            <div v-else class="d-flex flex-column ga-2">
                                <div v-if="simplefinErrors.length" class="pa-3 border-l-4 border-warning bg-warning-lighten-5">
                                    <div class="fw-medium">SimpleFin reports a connection issue:</div>
                                    <ul class="ml-4 mt-1 mb-2">
                                        <li v-for="(e, i) in simplefinErrors" :key="i">{{ e }}</li>
                                    </ul>
                                    <div class="fs--1 text-medium-emphasis">
                                        Re-authenticate at
                                        <a href="https://beta-bridge.simplefin.org/" target="_blank" rel="noopener">beta-bridge.simplefin.org</a>
                                        — your existing connection will resume once the upstream is healthy. No need to re-link here.
                                    </div>
                                    <div v-if="bankAccount?.additional_data?.simplefin_errors_checked_at" class="fs--1 text-medium-emphasis">
                                        Last checked: {{ bankAccount.additional_data.simplefin_errors_checked_at }}
                                    </div>
                                </div>
                                <div>
                                    Connected account:
                                    <code>{{ bankAccount?.additional_data?.simplefin_account_name || bankAccount?.additional_data?.simplefin_account_id }}</code>
                                </div>
                                <div v-if="bankAccount?.additional_data?.simplefin_consent_at" class="text-medium-emphasis fs--1">
                                    Consent recorded {{ bankAccount?.additional_data?.simplefin_consent_at }}
                                </div>
                                <div>
                                    <v-btn-input-compact color="btn-secondary" @click="disconnectSimplefin" :loading="sfLoading">
                                        <template #prepend>
                                            <mdi-icon>link_off</mdi-icon>
                                        </template>
                                        Disconnect SimpleFin
                                    </v-btn-input-compact>
                                </div>
                            </div>
                        </div>

                         <div v-if="form.errors.custom_error" class="fs-1 pb-2 text-error">
                             {{ form.errors.custom_error }}
                         </div>
                     </div>
                 </v-card-text>
                 <v-card-actions-form>
                     <div class="d-flex flex-grow-1 justify-space-between">
                         <v-btn-input-compact color="btn-secondary" @click="helpers.goBack">
                             <template #prepend>
                                 <mdi-icon>chevron_left</mdi-icon>
                             </template>
                             Back
                         </v-btn-input-compact>
                         <v-btn-input-compact type="submit" :disabled="form.processing" :loading="form.processing">
                             <template #prepend>
                                 <mdi-icon>check</mdi-icon>
                             </template>
                             Save
                         </v-btn-input-compact>
                     </div>

                 </v-card-actions-form>
             </v-card>
         </v-form>
     </div>
 </template>
 <script setup lang="ts">
     import { ref, computed } from "vue";
     import { useForm, usePage, router } from '@inertiajs/vue3';
     import { toast } from 'vue3-toastify';
     import { SharedPage, SharedPageProps } from '@/Types/inertia';
     import { User } from '@/Types/Models/user';
     import helpers from '@/Plugins/helpers';
     import { pageTitle, containerGapForForm } from '@/Composables/PageMeta';
     import { BankAccount } from "@/Types/Models/bank-account";
     import { useDate } from 'vuetify';
     import { useAuth } from '@/Composables/Auth';


     pageTitle.value = 'Add Bank Account';
     containerGapForForm();
     interface PageData extends SharedPageProps{
         data?:{
            bank_account?: BankAccount,
            success_redirect?: string
         }
     }


     interface Form extends BankAccount{
         custom_error: string,
         is_meow: boolean,
         meow_account_id: string,
         success_redirect: string,
     }

     const page = usePage<PageData>();
     const bankAccount = ref<BankAccount>(page.props.data?.bank_account ?? {} as BankAccount);
     const formRef = ref<HTMLFormElement | null>(null);
     const formValid = ref<boolean | null>(false);
     const form = useForm<Form>({
         custom_error: '',
         nickname: bankAccount.value?.nickname ?? '',
         account_name: bankAccount.value?.account_name ?? '',
         account_number: bankAccount.value?.account_number ?? '',
         bank_name: bankAccount.value?.bank_name ?? '',
         routing_number: bankAccount.value?.routing_number ?? '',
         swift_code: bankAccount.value?.swift_code ?? '',
         is_meow: bankAccount.value?.additional_data?.is_meow ?? false,
         meow_account_id: bankAccount.value?.additional_data?.meow_account_id ?? '',

         success_redirect: page.props.data?.success_redirect ?? '',
     });


     const store = async () => {
         var validated = await formRef.value?.validate();
         if(validated?.valid){
             form.post(route('bank_accounts.store', bankAccount.value?.id ?? 0), {
                 preserveState: true,
                 onSuccess: (page: SharedPage) => {
                     toast.success(page.props?.jetstream?.flash?.msg?.text ?? 'Bank account is saved');
                     formRef.value?.resetValidation();
                     form.clearErrors();
                 },
                 onFinish: (visit) => {
                 },
                 onError: (errors) => {
                     console.log('onError', errors);
                     formRef.value?.resetValidation();
                 }
             });
         }
         else{
             form.setError('custom_error', 'There are invalid data in the form, please check once again');
         }
     }

     // ---- Plaid Link ----
     const consent = ref(false);
     const plaidLoading = ref(false);
     const plaidConnected = computed(() => !!bankAccount.value?.additional_data?.is_plaid);

     const csrfToken = (): string =>
         document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') ?? '';

     const loadPlaidLink = (): Promise<void> =>
         new Promise((resolve, reject) => {
             if ((window as any).Plaid) return resolve();
             const script = document.createElement('script');
             script.src = 'https://cdn.plaid.com/link/v2/stable/link-initialize.js';
             script.onload = () => resolve();
             script.onerror = () => reject(new Error('Failed to load Plaid Link'));
             document.head.appendChild(script);
         });

     const connectPlaid = async () => {
         if (!bankAccount.value?.id || !consent.value) return;
         plaidLoading.value = true;
         try {
             await loadPlaidLink();

             const tokenRes = await fetch(route('bank_accounts.plaid.linkToken'), {
                 headers: { Accept: 'application/json' },
                 credentials: 'same-origin',
             });
             if (!tokenRes.ok) throw new Error('Could not obtain Plaid link token');
             const { link_token } = await tokenRes.json();
             if (!link_token) throw new Error('Plaid link token missing in response');

             const handler = (window as any).Plaid.create({
                 token: link_token,
                 onSuccess: async (public_token: string, metadata: any) => {
                     const plaidAccountId = metadata?.accounts?.[0]?.id;
                     if (!plaidAccountId) {
                         toast.error('Plaid did not return an account id');
                         return;
                     }
                     try {
                         const res = await fetch(route('bank_accounts.plaid.connect', bankAccount.value.id), {
                             method: 'POST',
                             credentials: 'same-origin',
                             headers: {
                                 'Content-Type': 'application/json',
                                 Accept: 'application/json',
                                 'X-CSRF-TOKEN': csrfToken(),
                             },
                             body: JSON.stringify({
                                 public_token,
                                 plaid_account_id: plaidAccountId,
                                 consent: true,
                             }),
                         });
                         if (!res.ok) throw new Error(await res.text());
                         toast.success('Plaid connected');
                         router.reload({ only: ['data'] });
                     } catch (e: any) {
                         toast.error('Plaid connect failed: ' + (e?.message ?? 'unknown'));
                     }
                 },
                 onExit: (err: any) => {
                     if (err) toast.error('Plaid Link exited: ' + (err?.display_message ?? err?.error_message ?? 'unknown'));
                 },
             });
             handler.open();
         } catch (e: any) {
             toast.error('Failed to start Plaid: ' + (e?.message ?? 'unknown'));
         } finally {
             plaidLoading.value = false;
         }
     };

     const disconnectPlaid = async () => {
         if (!bankAccount.value?.id) return;
         if (!confirm('Disconnect Plaid for this bank account? Pending reconciliations will stop.')) return;
         plaidLoading.value = true;
         try {
             const res = await fetch(route('bank_accounts.plaid.disconnect', bankAccount.value.id), {
                 method: 'POST',
                 credentials: 'same-origin',
                 headers: {
                     Accept: 'application/json',
                     'X-CSRF-TOKEN': csrfToken(),
                 },
             });
             if (!res.ok) throw new Error(await res.text());
             toast.success('Plaid disconnected');
             router.reload({ only: ['data'] });
         } catch (e: any) {
             toast.error('Disconnect failed: ' + (e?.message ?? 'unknown'));
         } finally {
             plaidLoading.value = false;
         }
     };

     // ---- SimpleFin ----
     const sfSetupToken = ref('');
     const sfConsent = ref(false);
     const sfLoading = ref(false);
     const simplefinConnected = computed(() => !!bankAccount.value?.additional_data?.is_simplefin);
     const simplefinErrors = computed<string[]>(() => {
         const errs = bankAccount.value?.additional_data?.simplefin_errors;
         return Array.isArray(errs) ? errs : [];
     });

     // ---- Automation toggle (any provider) ----
     const hasProvider = computed(() => !!(
         bankAccount.value?.additional_data?.is_meow ||
         bankAccount.value?.additional_data?.is_plaid ||
         bankAccount.value?.additional_data?.is_simplefin
     ));
     const automationEnabled = ref(!!bankAccount.value?.automate_transaction);
     const automationLoading = ref(false);

     const onAutomationToggle = async (newValue: boolean) => {
         if (!bankAccount.value?.id) return;
         const previous = automationEnabled.value;
         automationEnabled.value = newValue;
         automationLoading.value = true;
         try {
             const res = await fetch(route('bank_accounts.automation.toggle', bankAccount.value.id), {
                 method: 'POST',
                 credentials: 'same-origin',
                 headers: {
                     'Content-Type': 'application/json',
                     Accept: 'application/json',
                     'X-CSRF-TOKEN': csrfToken(),
                 },
                 body: JSON.stringify({ enabled: newValue }),
             });
             const json = await res.json().catch(() => ({}));
             if (!res.ok || json?.status === false) {
                 throw new Error(json?.error ?? 'Toggle failed');
             }
             toast.success(newValue ? 'Reconciliation automation ON' : 'Reconciliation automation OFF');
         } catch (e: any) {
             automationEnabled.value = previous;
             toast.error('Toggle failed: ' + (e?.message ?? 'unknown'));
         } finally {
             automationLoading.value = false;
         }
     };

     const connectSimplefin = async () => {
         if (!bankAccount.value?.id || !sfSetupToken.value || !sfConsent.value) return;
         sfLoading.value = true;
         try {
             const res = await fetch(route('bank_accounts.simplefin.connect', bankAccount.value.id), {
                 method: 'POST',
                 credentials: 'same-origin',
                 headers: {
                     'Content-Type': 'application/json',
                     Accept: 'application/json',
                     'X-CSRF-TOKEN': csrfToken(),
                 },
                 body: JSON.stringify({
                     setup_token: sfSetupToken.value.trim(),
                     consent: true,
                 }),
             });
             const json = await res.json().catch(() => ({}));
             if (!res.ok || json?.status === false) {
                 throw new Error(json?.error ?? 'SimpleFin connect failed');
             }
             const accountNote = json?.account_count > 1
                 ? ` (token had ${json.account_count} accounts; using "${json.account_name}")`
                 : '';
             const matchNote = json?.matched > 0
                 ? ` Auto-matched ${json.matched} pending invoice(s) (\$${json.matched_amount}) from history.`
                 : '';
             toast.success('SimpleFin connected.' + accountNote + matchNote);
             sfSetupToken.value = '';
             sfConsent.value = false;
             router.reload({ only: ['data'] });
         } catch (e: any) {
             toast.error('SimpleFin connect failed: ' + (e?.message ?? 'unknown'));
         } finally {
             sfLoading.value = false;
         }
     };

     const disconnectSimplefin = async () => {
         if (!bankAccount.value?.id) return;
         if (!confirm('Disconnect SimpleFin for this bank account? Pending reconciliations will stop.')) return;
         sfLoading.value = true;
         try {
             const res = await fetch(route('bank_accounts.simplefin.disconnect', bankAccount.value.id), {
                 method: 'POST',
                 credentials: 'same-origin',
                 headers: {
                     Accept: 'application/json',
                     'X-CSRF-TOKEN': csrfToken(),
                 },
             });
             if (!res.ok) throw new Error(await res.text());
             toast.success('SimpleFin disconnected');
             router.reload({ only: ['data'] });
         } catch (e: any) {
             toast.error('Disconnect failed: ' + (e?.message ?? 'unknown'));
         } finally {
             sfLoading.value = false;
         }
     };
 </script>
